import React, { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdmin } from '../redux/types/authTypes';
import toast from 'react-hot-toast';
import {
  MdLogin, MdLogout, MdDirectionsRun, MdCheckCircle, MdCancel, MdCalendarToday,
} from 'react-icons/md';
import DateNav from '../components/Attendance/DateNav';
import CustomSelect, { monthOptions0, yearOptions } from '../components/Attendance/CustomSelect';
import { BsCircleHalf, BsClockHistory } from 'react-icons/bs';
import {
  checkIn,
  checkOut,
  fetchMyCalendar,
  fetchMyLeaveRequests,
  fetchMyLeaveBalance,
  requestLeave,
  fetchMyYearlySummary,
} from '../redux/actions/attendanceActions';
import type { CalendarDay, LeaveRequest } from '../redux/types/attendanceTypes';
import ComponentWrapper from '../components/utils/ComponentWrapper';

// ── Helpers ───────────────────────────────────────────────────────────────────

const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];


const toYMD = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;


const fmtTime = (iso: string | null) => {
  if (!iso) return null;
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};


const ordSuffix = (n: number) => {
  if (n >= 11 && n <= 13) return 'th';
  return ['th','st','nd','rd','th'][Math.min(n % 10, 4)];
};

const fmtOrdinal = (iso: string) => {
  const d = new Date(iso); const n = d.getDate();
  return `${n}${ordSuffix(n)} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
};

const fmtCreatedAt = (iso: string) => {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
};

const daysBetween = (start: string, end: string) =>
  Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 86_400_000) + 1);

// ── Monitor illustrations ─────────────────────────────────────────────────────

const CheckInMonitor: React.FC = () => (
  <svg viewBox="0 0 140 110" className="w-28 h-20 sm:w-32 sm:h-24 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="2" width="130" height="82" rx="7" fill="#E0E8F8" stroke="#C8D5EC" strokeWidth="1.5"/>
    <rect x="13" y="10" width="114" height="66" rx="4" fill="#C8D8F0"/>
    <rect x="20" y="18" width="50" height="9" rx="3" fill="#A5C0E8"/>
    <rect x="20" y="33" width="38" height="7" rx="2" fill="#BFD3EE"/>
    <rect x="20" y="45" width="65" height="7" rx="2" fill="#BFD3EE"/>
    <rect x="20" y="57" width="45" height="7" rx="2" fill="#A5C0E8"/>
    <rect x="82" y="18" width="38" height="44" rx="4" fill="#94B4DC"/>
    <rect x="88" y="26" width="26" height="5" rx="2" fill="#C8DCF0"/>
    <rect x="88" y="36" width="20" height="5" rx="2" fill="#C8DCF0"/>
    <rect x="88" y="46" width="26" height="5" rx="2" fill="#C8DCF0"/>
    <rect x="57" y="84" width="26" height="11" rx="1" fill="#C8D5EC"/>
    <rect x="40" y="95" width="60" height="6" rx="3" fill="#C8D5EC"/>
  </svg>
);

const CheckOutMonitor: React.FC = () => (
  <svg viewBox="0 0 140 110" className="w-28 h-20 sm:w-32 sm:h-24 flex-shrink-0" xmlns="http://www.w3.org/2000/svg">
    <rect x="5" y="2" width="130" height="82" rx="7" fill="#2B3A55" stroke="#3D4F6B" strokeWidth="1.5"/>
    <rect x="13" y="10" width="114" height="66" rx="4" fill="#1A2740"/>
    <rect x="20" y="18" width="50" height="9" rx="3" fill="#2D4070"/>
    <rect x="20" y="33" width="38" height="7" rx="2" fill="#1F3060"/>
    <rect x="20" y="45" width="65" height="7" rx="2" fill="#1F3060"/>
    <rect x="20" y="57" width="45" height="7" rx="2" fill="#2D4070"/>
    <rect x="82" y="18" width="38" height="44" rx="4" fill="#1E4DB7"/>
    <rect x="88" y="26" width="26" height="5" rx="2" fill="#5B8DE8"/>
    <rect x="88" y="36" width="20" height="5" rx="2" fill="#4070CC"/>
    <rect x="88" y="46" width="26" height="5" rx="2" fill="#5B8DE8"/>
    <rect x="57" y="84" width="26" height="11" rx="1" fill="#3D4F6B"/>
    <rect x="40" y="95" width="60" height="6" rx="3" fill="#3D4F6B"/>
  </svg>
);

// ── Status badge ──────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'PRESENT':
      return (
        <span className="inline-flex items-center gap-1.5 text-green-600 bg-green-50 border border-green-200 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
          <MdCheckCircle className="text-base" /> Present
        </span>
      );
    case 'ABSENT':
      return (
        <span className="inline-flex items-center gap-1.5 text-red-500 bg-red-50 border border-red-200 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
          <MdCancel className="text-base" /> Absent
        </span>
      );
    case 'HALF_DAY':
      return (
        <span className="inline-flex items-center gap-1.5 text-purple-600 bg-purple-50 border border-purple-200 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
          <BsCircleHalf className="text-base" /> Half-Day
        </span>
      );
    case 'ON_LEAVE':
      return (
        <span className="inline-flex items-center gap-1.5 text-red-500 bg-red-50 border border-red-200 px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap">
          <MdDirectionsRun className="text-base" /> Leave
        </span>
      );
    default:
      return null;
  }
};

// ── Half-Day Period Badge ─────────────────────────────────────────────────────

const HalfDayBadge: React.FC<{ period: 'FIRST_HALF' | 'SECOND_HALF' | null | undefined }> = ({ period }) => {
  if (!period) return null;
  return (
    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full ${
      period === 'FIRST_HALF'
        ? 'bg-indigo-100 text-indigo-600'
        : 'bg-purple-100 text-purple-600'
    }`}>
      {period === 'FIRST_HALF' ? 'First Half' : 'Second Half'}
    </span>
  );
};

// ── Check-In / Check-Out Confirmation Modal ───────────────────────────────────

const AttendanceConfirmModal: React.FC<{
  mode: 'checkin' | 'checkout';
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
}> = ({ mode, onConfirm, onClose, loading }) => {
  const isCheckIn = mode === 'checkin';

  const [time, setTime] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7">
        <div className="flex items-center gap-3 mb-2">
          {isCheckIn
            ? <MdLogin className="text-[#3B3BB7] text-3xl" />
            : <MdLogout className="text-[#E0694B] text-3xl" />}
          <h2 className="text-2xl font-bold text-gray-900">
            {isCheckIn ? 'Check-In' : 'Check-Out'}
          </h2>
        </div>

        <p className="text-sm text-gray-500 mb-1">
          {isCheckIn ? 'Are you sure you want to check-in?' : 'Are you sure you want to check-out?'}
        </p>
        <p className="text-lg font-semibold text-gray-700 mb-6">{timeStr}</p>

        <div className="flex gap-3">
          <button onClick={onClose}
            className={`flex-1 border font-semibold py-2.5 rounded-xl transition-colors ${
              isCheckIn
                ? 'border-[#3B3BB7] text-[#3B3BB7] hover:bg-[#3B3BB7]/10'
                : 'border-[#E0694B] text-[#E0694B] hover:bg-[#E0694B]/10'
            }`}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className={`flex-1 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors ${
              isCheckIn
                ? 'bg-[#3B3BB7] hover:bg-[#2E2EA0]'
                : 'bg-[#E0694B] hover:bg-[#C85030]'
            }`}>
            {loading ? 'Please wait…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Custom Date Range Picker ──────────────────────────────────────────────────

const DAY_NAMES = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const CustomDatePicker: React.FC<{
  start: string;
  end: string;
  single?: boolean;
  onChange: (start: string, end: string) => void;
}> = ({ start, end, single, onChange }) => {
  const now = new Date();
  const [open,  setOpen]  = useState(false);
  const [vy,    setVy]    = useState(now.getFullYear());
  const [vm,    setVm]    = useState(now.getMonth());
  const [hover, setHover] = useState('');

  const pad  = (n: number) => String(n).padStart(2, '0');
  const toD  = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;
  const disp = (ymd: string) => {
    if (!ymd) return '';
    const d = new Date(ymd + 'T00:00:00');
    return `${d.getDate()} ${MONTH_SHORT[d.getMonth()]} ${d.getFullYear()}`;
  };

  const daysInMonth = new Date(vy, vm + 1, 0).getDate();
  const offset      = (new Date(vy, vm, 1).getDay() + 6) % 7; // Mon=0

  const handleDay = (day: number) => {
    const d = toD(vy, vm, day);
    if (single) { onChange(d, d); setOpen(false); return; }
    if (!start || (start && end)) { onChange(d, ''); }
    else {
      const [s, e] = d < start ? [d, start] : [start, d];
      onChange(s, e);
      setOpen(false);
    }
  };

  const inRange = (d: string) => start && end && d > start && d < end;
  const isStart = (d: string) => d === start;
  const isEnd   = (d: string) => d === end;
  const isHover = (d: string) => start && !end && hover && d > start && d <= hover;

  return (
    <div>
      {/* Trigger */}
      <div
        className="flex items-center gap-2 cursor-pointer"
        onClick={() => setOpen(o => !o)}
      >
        <div className="flex-1 flex items-center gap-2 text-sm">
          {single ? (
            <span className={start ? 'text-gray-700' : 'text-gray-300'}>
              {start ? disp(start) : 'Select date'}
            </span>
          ) : (
            <>
              <span className={start ? 'text-gray-700' : 'text-gray-300'}>
                {start ? disp(start) : 'From'}
              </span>
              <span className="text-gray-300">→</span>
              <span className={end ? 'text-gray-700' : 'text-gray-300'}>
                {end ? disp(end) : 'To'}
              </span>
            </>
          )}
        </div>
        <MdCalendarToday className="text-[#2D328F] text-lg flex-shrink-0" />
      </div>

      {/* Inline calendar */}
      {open && (
        <div className="mt-3 pt-3 border-t border-gray-100 select-none">
          {/* Month nav */}
          <div className="flex items-center justify-center gap-2 mb-3">
            <CustomSelect value={vm} options={monthOptions0()} onChange={setVm} />
            <CustomSelect
              value={vy}
              options={yearOptions(1900, new Date().getFullYear() + 1)}
              onChange={setVy}
            />
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_NAMES.map(d => (
              <div key={d} className="text-center text-[11px] font-medium text-gray-400 py-1">{d}</div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7">
            {Array.from({ length: offset }).map((_, i) => <div key={`b${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const d     = toD(vy, vm, day);
              const isSt  = isStart(d);
              const isEn  = isEnd(d);
              const inR   = inRange(d);
              const inH   = isHover(d);
              const today = d === toD(now.getFullYear(), now.getMonth(), now.getDate());

              return (
                <div key={day}
                  className={`flex items-center justify-center h-9 ${(inR || inH) ? 'bg-blue-50' : ''} ${isSt && !single ? 'rounded-l-full' : ''} ${isEn ? 'rounded-r-full' : ''}`}
                  onMouseEnter={() => !end && setHover(d)}
                  onMouseLeave={() => setHover('')}
                >
                  <button
                    type="button"
                    onClick={() => handleDay(day)}
                    className={`w-8 h-8 text-xs rounded-full flex items-center justify-center transition-colors font-medium
                      ${isSt || isEn
                        ? 'bg-[#3B3BB7] text-white'
                        : today
                        ? 'text-[#3B3BB7] border border-[#3B3BB7]'
                        : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>

          {!single && (
            <p className="text-[11px] text-gray-400 text-center mt-2">
              {!start ? 'Tap a start date' : !end ? 'Tap an end date' : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Leave Request Bottom Sheet ────────────────────────────────────────────────

const LeaveRequestModal: React.FC<{
  mode: 'leave' | 'halfday';
  loading: boolean;
  onSubmit: (data: { startDate: string; endDate: string; reason: string; halfDayPeriod?: 'FIRST_HALF' | 'SECOND_HALF' }) => void;
  onClose: () => void;
}> = ({ mode, loading, onSubmit, onClose }) => {
  const isHalfDay = mode === 'halfday';
  const accentColor = isHalfDay ? '#0698AD' : '#E05656';

  const [form, setForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
  });
  const [halfDayPeriod, setHalfDayPeriod] = useState<'FIRST_HALF' | 'SECOND_HALF'>('FIRST_HALF');

  // Slide-up animation
  const [visible, setVisible] = useState(false);
  useEffect(() => { const id = requestAnimationFrame(() => setVisible(true)); return () => cancelAnimationFrame(id); }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 280);
  };

  const valid = form.startDate && (isHalfDay || form.endDate) && form.reason.trim();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    onSubmit({
      ...form,
      endDate: isHalfDay ? form.startDate : form.endDate,
      halfDayPeriod: isHalfDay ? halfDayPeriod : undefined,
    });
  };

  // Shared fieldset style
  const fieldset = 'border border-gray-300 rounded-xl px-3 pb-3 pt-0';
  const legend   = 'text-xs text-gray-400 px-1 leading-none';
  const input    = 'w-full bg-transparent text-gray-700 text-sm focus:outline-none placeholder-gray-300';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onClick={handleClose}>
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`} />

      {/* Sheet */}
      <div
        onClick={e => e.stopPropagation()}
        className={`relative bg-white rounded-3xl shadow-2xl w-full max-w-lg transition-all duration-300 ease-out ${visible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'}`}
      >
        {/* Handle */}
        <div className="flex justify-center pt-4 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-100" />
        </div>

        <div className="px-6 pb-10 pt-3">
          {/* Title */}
          <div className="flex items-center justify-between gap-2 mb-6">
            <div className="flex items-center gap-2">
              <MdDirectionsRun className="text-3xl" style={{ color: accentColor }} />
              <h2 className="text-2xl font-bold" style={{ color: accentColor }}>
                {isHalfDay ? 'Request Half Day' : 'Request Leave'}
              </h2>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <MdCancel className="text-xl" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5">
            {/* Date */}
            <fieldset className={fieldset}>
              <legend className={legend}>Date <span className="text-red-500">*</span></legend>
              <div className="pt-1">
                <CustomDatePicker
                  start={form.startDate}
                  end={form.endDate}
                  single={isHalfDay}
                  onChange={(s, e) => setForm(f => ({ ...f, startDate: s, endDate: e }))}
                />
              </div>
            </fieldset>

            {/* Half-day period */}
            {isHalfDay && (
              <fieldset className={fieldset}>
                <legend className={legend}>Half-day period</legend>
                <div className="flex gap-6 pt-2">
                  {(['FIRST_HALF', 'SECOND_HALF'] as const).map(opt => (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="halfDayPeriod"
                        value={opt}
                        checked={halfDayPeriod === opt}
                        onChange={() => setHalfDayPeriod(opt)}
                        className="accent-[#0698AD]"
                      />
                      <span className="text-sm text-gray-700">
                        {opt === 'FIRST_HALF' ? 'First Half (Morning)' : 'Second Half (Afternoon)'}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {/* Description */}
            <fieldset className={fieldset}>
              <legend className={legend}>Description <span className="text-red-500">*</span></legend>
              <textarea
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Enter Description"
                rows={4}
                className={`${input} resize-none pt-1`}
              />
            </fieldset>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !valid}
              className="w-full text-white font-semibold py-4 rounded-2xl text-base disabled:opacity-50 transition-opacity mt-1"
              style={{ backgroundColor: accentColor }}
            >
              {loading ? 'Submitting…' : (isHalfDay ? 'Request Half Day' : 'Request Leave')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

// ── Leave History Modal ───────────────────────────────────────────────────────

const LeaveHistoryModal: React.FC<{
  requests: LeaveRequest[];
  onClose: () => void;
}> = ({ requests, onClose }) => {
  const statusCls: Record<string, string> = {
    PENDING:  'bg-yellow-100 text-yellow-700',
    APPROVED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-800">Leave History</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        {requests.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No leave requests yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {requests.map(r => (
              <div key={r.id} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-semibold text-sm text-gray-800">Leave</span>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${statusCls[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {r.status}
                  </span>
                </div>
                <div className="flex items-start gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">From</p>
                    <p className="font-semibold text-gray-800">{fmtOrdinal(r.startDate)}</p>
                  </div>
                  <div className="flex-1 flex flex-col items-center pt-3">
                    <div className="w-full border-t border-dashed border-gray-300" />
                    <span className="text-[10px] text-gray-400 mt-1">
                      {daysBetween(r.startDate, r.endDate)} DAYS
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-gray-400">To</p>
                    <p className="font-semibold text-gray-800">{fmtOrdinal(r.endDate)}</p>
                  </div>
                </div>
                <div className="mt-2">
                  <HalfDayBadge period={r.halfDayPeriod} />
                </div>
                {(r.reviewedBy || r.reviewedByAdmin) && (
                  <p className="text-xs text-gray-400 mt-2">
                    Reviewed by:{' '}
                    <span className="font-medium text-gray-600">
                      {r.reviewedByAdmin?.fullname ?? r.reviewedBy?.fullname}
                    </span>
                  </p>
                )}
                {r.reviewNote && (
                  <p className="text-xs text-gray-400 mt-1 italic">"{r.reviewNote}"</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Monthly Calendar (same style as AttendanceCalendarView) ──────────────────

const CAL_STATUS: Record<string, { cell: string; dot: string; label: string; text: string }> = {
  PRESENT:  { cell: 'bg-emerald-50 border-emerald-200',  dot: 'bg-emerald-400', label: 'Present',  text: 'text-emerald-600' },
  ABSENT:   { cell: 'bg-red-50 border-red-200',          dot: 'bg-red-400',     label: 'Absent',   text: 'text-red-500'     },
  HALF_DAY: { cell: 'bg-sky-50 border-sky-200',          dot: 'bg-sky-400',     label: 'Half Day', text: 'text-sky-600'     },
  ON_LEAVE: { cell: 'bg-amber-50 border-amber-200',      dot: 'bg-amber-400',   label: 'On Leave', text: 'text-amber-600'   },
  WEEKEND:  { cell: 'bg-gray-50 border-gray-100',        dot: '',               label: '',         text: 'text-gray-300'    },
  UPCOMING: { cell: 'bg-white border-gray-100',          dot: '',               label: '',         text: 'text-gray-800'    },
};

const DAY_NAMES_CAL = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MyMonthCalendar: React.FC<{
  calendar: CalendarDay[];
  year: number;
  month: number;
  loading: boolean;
  onMonthChange: (m: number) => void;
  onYearChange: (y: number) => void;
}> = ({ calendar, year, month, loading, onMonthChange, onYearChange }) => {
  const today = new Date();

  let firstDay = new Date(year, month - 1, 1).getDay();
  firstDay = firstDay === 0 ? 6 : firstDay - 1;
  const totalDays      = new Date(year, month, 0).getDate();
  const trailingBlanks = (7 - ((firstDay + totalDays) % 7)) % 7;

  const dayMap = new Map(calendar.map(d => [new Date(d.date).getUTCDate(), d]));

  const present = calendar.filter(d => d.status === 'PRESENT').length;
  const absent  = calendar.filter(d => d.status === 'ABSENT').length;
  const onLeave = calendar.filter(d => d.status === 'ON_LEAVE').length;
  const halfDay = calendar.filter(d => d.status === 'HALF_DAY').length;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">My Attendance</h2>
        <DateNav
          month={month}
          year={year}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
        />
      </div>

      {/* Summary pills */}
      {!loading && (
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            { label: 'Present',  count: present, ...CAL_STATUS.PRESENT  },
            { label: 'Absent',   count: absent,  ...CAL_STATUS.ABSENT   },
            { label: 'On Leave', count: onLeave, ...CAL_STATUS.ON_LEAVE },
            { label: 'Half Day', count: halfDay, ...CAL_STATUS.HALF_DAY },
          ].map(s => (
            <div key={s.label}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold ${s.cell} ${s.text}`}>
              <span className={`w-2 h-2 rounded-full ${s.dot}`} />
              {s.label} <span className="font-bold">{s.count}</span>
            </div>
          ))}
        </div>
      )}

      {/* Grid */}
      <div className={`bg-gray-50 rounded-2xl p-3 sm:p-4 transition-opacity ${loading ? 'opacity-40' : ''}`}>
        {/* Day headers */}
        <div className="grid grid-cols-7 mb-2">
          {DAY_NAMES_CAL.map(d => (
            <div key={d} className="text-center text-[11px] font-semibold text-gray-400 py-2 tracking-wide uppercase">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 sm:gap-1.5">
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`b${i}`} className="rounded-xl min-h-[52px] sm:min-h-[76px]" />
          ))}

          {Array.from({ length: totalDays }, (_, i) => {
            const date     = i + 1;
            const day      = dayMap.get(date);
            const status   = day?.status ?? 'UPCOMING';
            const style    = CAL_STATUS[status] ?? CAL_STATUS.UPCOMING;
            const isToday  = year === today.getFullYear() && month === today.getMonth() + 1 && date === today.getDate();
            const isWeekend  = status === 'WEEKEND';
            const isUpcoming = status === 'UPCOMING';

            return (
              <div key={date}
                title={day && !isWeekend && !isUpcoming ? [
                  style.label,
                  day.checkInTime  ? `In: ${fmtTime(day.checkInTime)}`   : '',
                  day.checkOutTime ? `Out: ${fmtTime(day.checkOutTime)}` : '',
                  day.workingHours ? `${day.workingHours.toFixed(1)}h`    : '',
                ].filter(Boolean).join(' · ') : ''}
                className={`relative rounded-xl border min-h-[52px] sm:min-h-[76px] p-1.5 sm:p-2 flex flex-col
                  ${isToday ? 'ring-2 ring-[#3B3BB7] ring-offset-1' : ''}
                  ${style.cell}`}
              >
                <span className={`text-[11px] sm:text-sm font-semibold leading-none
                  ${isToday ? 'text-[#3B3BB7]' : isWeekend ? 'text-gray-300' : 'text-gray-700'}`}>
                  {date}
                </span>
                {!isWeekend && !isUpcoming && day && (
                  <>
                    <span className={`hidden sm:block mt-auto text-[10px] font-semibold ${style.text}`}>
                      {day.workingHours !== null ? `${day.workingHours.toFixed(1)}h` : style.label}
                    </span>
                    <span className={`sm:hidden mt-auto mx-auto w-2 h-2 rounded-full ${style.dot}`} />
                  </>
                )}
                {isToday && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#3B3BB7]" />}
              </div>
            );
          })}

          {Array.from({ length: trailingBlanks }).map((_, i) => (
            <div key={`e${i}`} className="rounded-xl min-h-[52px] sm:min-h-[76px]" />
          ))}
        </div>
      </div>

      {loading && <p className="text-center text-sm text-gray-400 mt-3">Loading…</p>}
    </div>
  );
};

// ── Yearly Attendance View ────────────────────────────────────────────────────

const MONTH_NAMES_FULL = ['January','February','March','April','May','June',
                          'July','August','September','October','November','December'];

const YearlyAttendanceView: React.FC<{
  summary: import('../redux/types/attendanceTypes').YearlySummary | null;
  year: number;
  loading: boolean;
  onYearChange: (y: number) => void;
}> = ({ summary, year, loading, onYearChange }) => {
  const t = summary?.totals;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900">My Attendance — {year}</h2>
        <CustomSelect
          value={year}
          options={yearOptions(1900, new Date().getFullYear())}
          onChange={onYearChange}
        />
      </div>

      {/* Summary cards */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : t ? (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Present',   value: t.present,  cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { label: 'Absent',    value: t.absent,   cls: 'bg-red-50 border-red-200 text-red-600'             },
            { label: 'Half Days', value: t.halfDay,  cls: 'bg-sky-50 border-sky-200 text-sky-700'             },
            { label: 'On Leave',  value: t.onLeave,  cls: 'bg-amber-50 border-amber-200 text-amber-700'       },
            { label: 'Total Hrs', value: `${t.totalWorkingHours.toFixed(0)}h`, cls: 'bg-purple-50 border-purple-200 text-purple-700' },
          ].map(card => (
            <div key={card.label} className={`border rounded-xl p-3 flex flex-col items-center justify-center text-center ${card.cls}`}>
              <p className="text-2xl font-bold">{card.value}</p>
              <p className="text-xs font-medium mt-0.5 opacity-70">{card.label}</p>
            </div>
          ))}
        </div>
      ) : null}

      {/* Month-by-month table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-xs text-gray-400 uppercase tracking-wide">
              <th className="text-left pb-2 pr-4 font-semibold">Month</th>
              <th className="text-center pb-2 px-2 font-semibold text-emerald-600">Present</th>
              <th className="text-center pb-2 px-2 font-semibold text-red-500">Absent</th>
              <th className="text-center pb-2 px-2 font-semibold text-sky-600">Half Days</th>
              <th className="text-center pb-2 px-2 font-semibold text-amber-600">On Leave</th>
              <th className="text-center pb-2 px-2 font-semibold text-purple-600">Hrs</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 12 }).map((_, i) => (
                <tr key={i} className="border-b border-gray-50">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <td key={j} className="py-2 px-2"><div className="h-4 rounded bg-gray-100 animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : summary?.monthly.map(m => (
              <tr key={m.month} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-2 pr-4 font-medium text-gray-800">{MONTH_NAMES_FULL[m.month - 1]}</td>
                <td className="py-2 px-2 text-center text-emerald-700 font-semibold">{m.present}</td>
                <td className="py-2 px-2 text-center text-red-500 font-semibold">{m.absent}</td>
                <td className="py-2 px-2 text-center text-sky-600 font-semibold">{m.halfDay}</td>
                <td className="py-2 px-2 text-center text-amber-600 font-semibold">{m.onLeave}</td>
                <td className="py-2 px-2 text-center text-purple-600 font-semibold">{m.totalWorkingHours.toFixed(0)}h</td>
              </tr>
            ))}
            {/* Totals row */}
            {t && (
              <tr className="bg-gray-50 font-bold text-gray-800 border-t border-gray-200">
                <td className="py-2 pr-4">Total</td>
                <td className="py-2 px-2 text-center text-emerald-700">{t.present}</td>
                <td className="py-2 px-2 text-center text-red-500">{t.absent}</td>
                <td className="py-2 px-2 text-center text-sky-600">{t.halfDay}</td>
                <td className="py-2 px-2 text-center text-amber-600">{t.onLeave}</td>
                <td className="py-2 px-2 text-center text-purple-600">{t.totalWorkingHours.toFixed(0)}h</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const Attendence: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(state => state.auth);
  const { todayRecord, calendar, leaveBalance, leaveRequests, loading, yearlySummary, yearlySummaryLoading } =
    useAppSelector(state => state.attendance);

  const admin = isAdmin(user);

  const now = new Date();
  const [calYear,  setCalYear]  = useState(now.getFullYear());
  const [calMonth, setCalMonth] = useState(now.getMonth() + 1);
  const [viewMode,   setViewMode]   = useState<'monthly' | 'yearly'>('monthly');
  const [yearlyYear, setYearlyYear] = useState(now.getFullYear());
  const [leaveModal,    setLeaveModal]    = useState<null | 'leave' | 'halfday'>(null);
  const [historyModal,  setHistoryModal]  = useState(false);
  const [attendConfirm, setAttendConfirm] = useState<null | 'checkin' | 'checkout'>(null);

  // Fetch leave meta on mount
  useEffect(() => {
    dispatch(fetchMyLeaveRequests());
    dispatch(fetchMyLeaveBalance({}));
  }, []);

  // Re-fetch calendar when month changes
  useEffect(() => {
    dispatch(fetchMyCalendar({ year: calYear, month: calMonth }));
  }, [calYear, calMonth]);

  // Fetch yearly summary when yearly view is active
  useEffect(() => {
    if (viewMode === 'yearly') {
      dispatch(fetchMyYearlySummary({ year: yearlyYear }));
    }
  }, [viewMode, yearlyYear]);

  const sortedRequests = useMemo(
    () => [...leaveRequests].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [leaveRequests]
  );
  const latestRequest = sortedRequests[0];

  const leaveBalancePrimary = leaveBalance.find(b => !b.leaveType.name.toLowerCase().includes('half')) ?? leaveBalance[0];
  const leaveBalanceHalf    = leaveBalance.find(b =>  b.leaveType.name.toLowerCase().includes('half'));

  // Fall back to calendar entry for today if todayRecord not yet populated (e.g. fresh page load)
  const todayCalEntry = useMemo(() => {
    const key = toYMD(now);
    return calendar.find(d => d.date.slice(0, 10) === key) ?? null;
  }, [calendar]);

  const todayCheckIn    = todayRecord?.checkInTime  ?? todayCalEntry?.checkInTime  ?? null;
  const todayCheckOut   = todayRecord?.checkOutTime ?? todayCalEntry?.checkOutTime ?? null;
  const todayHours      = todayRecord?.workingHours ?? todayCalEntry?.workingHours ?? null;
  const checkedIn  = !!todayCheckIn;
  const checkedOut = !!todayCheckOut;

  const handleCheckIn = async () => {
    const r = await dispatch(checkIn());
    setAttendConfirm(null);
    if (checkIn.fulfilled.match(r)) {
      toast.success('Checked in!');
      dispatch(fetchMyCalendar({ year: calYear, month: calMonth }));
    } else {
      toast.error((r.payload as string) ?? 'Check-in failed');
    }
  };

  const handleCheckOut = async () => {
    const r = await dispatch(checkOut());
    setAttendConfirm(null);
    if (checkOut.fulfilled.match(r)) {
      toast.success('Checked out!');
      dispatch(fetchMyCalendar({ year: calYear, month: calMonth }));
    } else {
      toast.error((r.payload as string) ?? 'Check-out failed');
    }
  };

  const handleLeaveSubmit = async (data: { startDate: string; endDate: string; reason: string; halfDayPeriod?: 'FIRST_HALF' | 'SECOND_HALF' }) => {
    const r = await dispatch(requestLeave(data));
    if (requestLeave.fulfilled.match(r)) {
      toast.success('Leave request submitted!');
      setLeaveModal(null);
      dispatch(fetchMyLeaveRequests());
      dispatch(fetchMyLeaveBalance({}));
    } else {
      toast.error((r.payload as string) ?? 'Failed to submit');
    }
  };

  const fullName = (user as any)?.fullname ?? (user as any)?.name ?? 'there';
  const todayFormatted = `${String(now.getDate()).padStart(2,'0')}, ${MONTH_SHORT[now.getMonth()]} ${now.getFullYear()}`;

  // Admin view
  if (admin) {
    return (
      <ComponentWrapper title="Attendance">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 flex flex-col items-center justify-center text-center gap-3">
          <div className="text-5xl">📋</div>
          <p className="text-gray-600 font-medium">Admins are not part of the check-in/check-out system.</p>
          <p className="text-sm text-gray-400">
            Manage employee attendance at{' '}
            <a href="/manage-attendance" className="text-blue-600 underline hover:text-blue-800">Manage Attendance</a>.
          </p>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <>
      {attendConfirm && (
        <AttendanceConfirmModal
          mode={attendConfirm}
          loading={loading}
          onConfirm={attendConfirm === 'checkin' ? handleCheckIn : handleCheckOut}
          onClose={() => setAttendConfirm(null)}
        />
      )}
      {leaveModal && (
        <LeaveRequestModal
          mode={leaveModal}
          loading={loading}
          onSubmit={handleLeaveSubmit}
          onClose={() => setLeaveModal(null)}
        />
      )}
      {historyModal && (
        <LeaveHistoryModal requests={sortedRequests} onClose={() => setHistoryModal(false)} />
      )}

      <ComponentWrapper
        title={`Hey, ${fullName}`}
        buttonOne={
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm">
            <MdCalendarToday className="text-[#2D328F] text-base" />
            <span className="text-sm font-semibold text-[#2D328F]">{todayFormatted}</span>
          </div>
        }
      >
        <div className="flex flex-col gap-5">

            {/* ── Check-In / Check-Out cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Check-In */}
              <div className="bg-[#3B3BB7]/5 border-2 border-[#3B3BB7] rounded-2xl p-5 flex items-center gap-4 overflow-hidden">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <MdLogin className="text-[#3B3BB7] text-xl flex-shrink-0" />
                    <h3 className="text-xl font-bold text-[#3B3BB7]">Check-In</h3>
                  </div>
                  <p className="text-sm text-[#3B3BB7]/60 mb-4">Check-In to start your day</p>
                  {checkedIn ? (
                    <div className="bg-[#3B3BB7]/10 rounded-xl px-4 py-2.5 text-center">
                      <p className="text-sm font-semibold text-[#3B3BB7]">
                        Checked in at {fmtTime(todayCheckIn) ?? '—'}
                      </p>
                    </div>
                  ) : (
                    <button onClick={() => setAttendConfirm('checkin')} disabled={loading}
                      className="w-full bg-[#3B3BB7] hover:bg-[#2E2EA0] disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl transition-colors text-sm">
                      Check-In
                    </button>
                  )}
                </div>
                <CheckInMonitor />
              </div>

              {/* Check-Out */}
              <div className="bg-[#E0694B]/5 border-2 border-[#E0694B] rounded-2xl p-5 flex items-center gap-4 overflow-hidden">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <MdLogout className="text-[#E0694B] text-xl flex-shrink-0" />
                    <h3 className="text-xl font-bold text-[#E0694B]">Check-Out</h3>
                  </div>
                  <p className="text-sm text-[#E0694B]/60 mb-4">Check-Out to end your day.</p>
                  {checkedOut ? (
                    <div className="bg-[#E0694B]/10 rounded-xl px-4 py-2.5 text-center">
                      <p className="text-sm font-semibold text-[#E0694B]">
                        Checked out at {fmtTime(todayCheckOut) ?? '—'}
                        {todayHours != null && (
                          <span className="ml-1.5 text-xs opacity-70">
                            · {todayHours.toFixed(1)}h
                          </span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <button onClick={() => setAttendConfirm('checkout')} disabled={loading || !checkedIn}
                      className="w-full font-semibold py-2.5 rounded-xl transition-colors text-sm text-white disabled:cursor-not-allowed"
                      style={{ backgroundColor: checkedIn ? '#E0694B' : '#E8927A' }}>
                      Check-Out
                    </button>
                  )}
                </div>
                <CheckOutMonitor />
              </div>
            </div>

            {/* ── Leave / Half-Day / Leave Status ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {/* Leave */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <MdDirectionsRun className="text-red-500 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-red-500">Leave</h3>
                    <p className="text-xs text-gray-400">Request leave time for yourself</p>
                  </div>
                </div>
                {/* Leave balance — hidden until restriction feature is ready */}
                {false && (
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-red-400">Availed</span>
                      <span className="flex items-center gap-3">
                        <span className="text-gray-300 text-base">—</span>
                        <span className="text-2xl font-bold text-red-500">{leaveBalancePrimary?.usedDays ?? 0}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-red-400">Remaining</span>
                      <span className="flex items-center gap-3">
                        <span className="text-gray-300 text-base">—</span>
                        <span className="text-2xl font-bold text-red-500">
                          {leaveBalancePrimary ? leaveBalancePrimary.totalDays - leaveBalancePrimary.usedDays : 0}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
                <button onClick={() => setLeaveModal('leave')}
                  className="w-full border border-red-400 text-red-500 hover:bg-red-50 font-semibold py-2.5 rounded-xl transition-colors text-sm">
                  Request Leave
                </button>
              </div>

              {/* Half-Day */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 flex flex-col gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <BsCircleHalf className="text-[#0698AD] text-xl" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0698AD]">Half-Day</h3>
                    <p className="text-xs text-gray-400">Request a half day</p>
                  </div>
                </div>
                {/* Half-day balance — hidden until restriction feature is ready */}
                {false && (
                  <div className="flex flex-col gap-2.5">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#0698AD]">Availed</span>
                      <span className="flex items-center gap-3">
                        <span className="text-gray-300 text-base">—</span>
                        <span className="text-2xl font-bold text-[#0698AD]">{leaveBalanceHalf?.usedDays ?? 0}</span>
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-[#0698AD]">Remaining</span>
                      <span className="flex items-center gap-3">
                        <span className="text-gray-300 text-base">—</span>
                        <span className="text-2xl font-bold text-[#0698AD]">
                          {leaveBalanceHalf ? leaveBalanceHalf.totalDays - leaveBalanceHalf.usedDays : 0}
                        </span>
                      </span>
                    </div>
                  </div>
                )}
                <button onClick={() => setLeaveModal('halfday')}
                  className="w-full border border-[#0698AD] text-[#0698AD] hover:bg-teal-50 font-semibold py-2.5 rounded-xl transition-colors text-sm">
                  Request Half Day
                </button>
              </div>

              {/* Leave Status */}
              <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 flex flex-col gap-3 sm:col-span-2 xl:col-span-1">
                  {/* Header with history button inline */}
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <BsClockHistory className="text-gray-700 text-xl" />
                      <h3 className="text-xl font-bold text-gray-800">Leave Status</h3>
                    </div>
                    <button onClick={() => setHistoryModal(true)}
                      className="flex items-center gap-1.5 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 px-3 py-1.5 rounded-full transition-colors flex-shrink-0">
                      <BsClockHistory className="text-sm" /> View History
                    </button>
                  </div>

                  {latestRequest ? (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-[#B98500] text-sm">Leave Request</p>
                          <p className="text-xs text-gray-400 mt-0.5">
                            Requested on: {fmtCreatedAt(latestRequest.createdAt)}
                          </p>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${
                          latestRequest.status === 'PENDING'
                            ? 'bg-gray-100 text-gray-500 border border-gray-200'
                            : latestRequest.status === 'APPROVED'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {latestRequest.status === 'PENDING' && <span className="text-[10px]">⏱</span>}
                          {latestRequest.status.charAt(0) + latestRequest.status.slice(1).toLowerCase()}
                        </span>
                      </div>

                      <div className="flex items-start gap-2 text-xs mt-1">
                        <div>
                          <p className="text-[10px] text-gray-400">From</p>
                          <p className="font-bold text-gray-800 text-sm">{fmtOrdinal(latestRequest.startDate)}</p>
                        </div>
                        <div className="flex-1 flex flex-col items-center pt-3">
                          <div className="w-full border-t-2 border-dashed border-gray-200" />
                          <span className="text-[10px] text-gray-400 mt-1">
                            {daysBetween(latestRequest.startDate, latestRequest.endDate)} DAYS
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] text-gray-400">To</p>
                          <p className="font-bold text-gray-800 text-sm">{fmtOrdinal(latestRequest.endDate)}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400 flex-1 flex items-center">No leave requests yet.</p>
                  )}
                </div>
            </div>

            {/* ── View toggle ── */}
            <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
              {(['monthly', 'yearly'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors capitalize ${
                    viewMode === mode
                      ? 'bg-white shadow text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>

            {/* ── Calendar / Yearly View ── */}
            {viewMode === 'monthly' ? (
              <MyMonthCalendar
                calendar={calendar}
                year={calYear}
                month={calMonth}
                loading={loading}
                onMonthChange={setCalMonth}
                onYearChange={setCalYear}
              />
            ) : (
              <YearlyAttendanceView
                summary={yearlySummary}
                year={yearlyYear}
                loading={yearlySummaryLoading}
                onYearChange={setYearlyYear}
              />
            )}
        </div>
      </ComponentWrapper>
    </>
  );
};

export default Attendence;
